import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { queueInspectorService } from '@/api/queue-inspector.service';
import type { Ref } from 'vue';
import { computed, toValue } from 'vue';
import type { QueueJobStatus } from '@carvia/ros-client-types';

export const useAvailableQueues = () => {
  return useQuery({
    queryKey: ['queues'],
    queryFn: () => queueInspectorService.getAvailableQueues(),
  });
};

export const useQueueStats = (queueName: Ref<string> | string) => {
  return useQuery({
    queryKey: ['queue-stats', queueName],
    queryFn: () => queueInspectorService.getQueueStats(toValue(queueName)),
    enabled: computed(() => !!toValue(queueName)),
  });
};

export const useQueueJobs = (
  queueName: Ref<string> | string,
  statuses: Ref<QueueJobStatus[]> | QueueJobStatus[],
) => {
  return useInfiniteQuery({
    queryKey: ['queue-jobs', queueName, statuses],
    queryFn: ({ pageParam = 1 }) =>
      queueInspectorService.getQueueJobs(toValue(queueName), toValue(statuses), pageParam),
    getNextPageParam: (lastPage) => lastPage.meta.nextPage,
    enabled: computed(() => !!toValue(queueName) && toValue(statuses).length > 0),
    initialPageParam: 1,
  });
};

export const useJobLogs = (queueName: Ref<string> | string, jobId: Ref<string> | string) => {
  return useQuery({
    queryKey: ['job-logs', queueName, jobId],
    queryFn: () => queueInspectorService.getJobLogs(toValue(queueName), toValue(jobId)),
    enabled: computed(() => !!toValue(queueName) && !!toValue(jobId)),
  });
};

export const useRetryJob = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ queueName, jobId }: { queueName: string; jobId: string }) =>
      queueInspectorService.retryJob(queueName, jobId),
    onSuccess: (_, { queueName }) => {
      // Invalidate and refetch queue stats and jobs
      queryClient.invalidateQueries({ queryKey: ['queue-stats', queueName] });
      queryClient.invalidateQueries({ queryKey: ['queue-jobs', queueName] });
    },
  });
};

export const useDeleteJob = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ queueName, jobId }: { queueName: string; jobId: string }) =>
      queueInspectorService.deleteJob(queueName, jobId),
    onSuccess: (_, { queueName }) => {
      queryClient.invalidateQueries({ queryKey: ['queue-stats', queueName] });
      queryClient.invalidateQueries({ queryKey: ['queue-jobs', queueName] });
    },
  });
};
